// Residential Masonry service data — brickwork, parging, foundation repair, underpinning.
// Hero and material images are photorealistic Higgsfield-generated stock
// photos (frontend/public/images/residential/), one per section.

export const MASONRY_SERVICE = {
    slug: "masonry",
    category: "Exterior · Masonry",
    seoHeading: "Masonry, Brick Repair, Parging & Underpinning · GTA",
    name: "Masonry",
    heroTitle: "Brick, block, and foundations done right.",
    heroSubhead:
        "Brickwork, parging, foundation repair, and underpinning that protect your home — and your biggest investment — for the long haul.",
    heroImage: "/images/residential/masonry-hero.63a67f5e.webp",
    heroImageAlt: "Restored brick front elevation and repointed foundation of a two-storey Ontario house",
    intro:
        "Your home's brick and foundation do quiet, heavy work every single day — and when something starts to crack, crumble, or leak, it rarely gets better on its own. As a trusted masonry contractor in Brampton and across the GTA, we handle everything from a single loose brick to lowering an entire basement, with honest assessments and tidy, durable workmanship you can actually see.",
    valueBullets: [
        {
            icon: "ShieldCheck",
            title: "We diagnose before we dig",
            body: "We tell you whether a crack is cosmetic or structural — and never sell you work your home doesn't need.",
        },
        {
            icon: "Hammer",
            title: "Matched brick & mortar",
            body: "We colour-match brick and mix mortar to your era of home so repairs blend in, not stand out.",
        },
        {
            icon: "Award",
            title: "Foundations done to code",
            body: "Crack injection, parging, and underpinning carried out to Ontario standards and inspected the right way.",
        },
        {
            icon: "Clock",
            title: "Workmanship warranty",
            body: "Our brick and foundation repairs are backed in writing — if it fails on us, we come back and fix it.",
        },
    ],
    materials: [
        {
            name: "Brickwork",
            desc: "Brick repair, tuckpointing and repointing, brick replacement, chimney brickwork, and decorative brick — restoring strength and curb appeal.",
            image: "/images/residential/masonry-m1.69aaaad9.webp",
            alt: "Replaced bricks and tuckpointed joints blending into the original bond pattern",
        },
        {
            name: "Parging",
            desc: "Foundation parging, re-parging, waterproof parge coatings, and crack or spall repair to seal and dress the exposed concrete around your home.",
            image: "/images/residential/masonry-m2.f3b0d94b.webp",
            alt: "Smooth grey parge coating applied over an exposed block foundation to a clean line",
        },
        {
            name: "Foundation Repairs",
            desc: "Epoxy and polyurethane crack injection, settlement repair, exterior waterproofing, and structural patching to stop leaks and stabilize your home.",
            image: "/images/residential/masonry-m3.dd9d51e0.webp",
            alt: "Basement wall crack sealed with injection ports and cured polyurethane resin",
        },
        {
            name: "Underpinning",
            desc: "Basement lowering and bench footing to add headroom and structural support — turning a cramped cellar into livable, code-compliant space.",
            image: "/images/residential/masonry-m4.e9b3759c.webp",
            alt: "New concrete bench footings stepping along a basement wall lowered for headroom",
        },
    ],
    processSteps: [
        {
            num: "01",
            title: "Free On-Site Assessment",
            body: "We come out, inspect the brick, foundation, and any cracks, and tell you plainly what's going on — no scare tactics.",
        },
        {
            num: "02",
            title: "Detailed Proposal",
            body: "You get a clear, line-item proposal: scope, materials, method, and a fixed price with no surprises.",
        },
        {
            num: "03",
            title: "Build/Repair",
            body: "Our crew shows up on schedule, protects your property, and does the brickwork or foundation work cleanly and to code.",
        },
        {
            num: "04",
            title: "Final Walk-Through",
            body: "We walk the finished work with you, confirm everything is sealed and solid, and leave the site tidy.",
        },
    ],
    faqs: [
        {
            q: "How much does tuckpointing cost in the GTA?",
            a: "Tuckpointing cost depends on how much mortar needs raking out and replacing, the height and access to the wall, and whether colour-matched mortar is required. As a rough guide, small repairs in **Mississauga** and the wider GTA often start in the few-hundred-dollar range, while a full wall or chimney can run into the low thousands. The honest answer is that pricing varies by condition, so we give you a fixed quote after seeing it in person rather than a phone-call guess. Done right, fresh tuckpointing protects your brick for decades and is far cheaper than letting water get behind the wall.",
        },
        {
            q: "What is parging and does my foundation need it?",
            a: "Parging is the thin cement coat applied over the exposed portion of your foundation — the strip of concrete or block between the ground and your brick. It protects that surface from water, frost, and weather, and gives a clean finished look. If your parging is cracking, flaking, or falling off in sheets, it's no longer doing its job and moisture can start reaching the foundation itself. For homeowners in **Toronto** and surrounding areas where freeze-thaw cycles are brutal, a sound parging foundation coat (or a fresh waterproof re-parge) is one of the simplest, lowest-cost ways to extend the life of your foundation.",
        },
        {
            q: "How do I know if my foundation crack is serious?",
            a: "Not every crack is an emergency. Thin, vertical hairline cracks are common as concrete cures and are often cosmetic. The ones to watch are horizontal cracks, stair-step cracks through block, cracks wider than a credit card, cracks that are growing, or any crack that lets water into your basement. Those can point to settlement or pressure issues and call for proper foundation crack repair in **Ontario** — typically epoxy or polyurethane injection, and sometimes structural patching. The safest move is a free assessment: we'll measure it, tell you whether it's structural or just leaking, and only recommend repair if it genuinely needs it.",
        },
        {
            q: "What is underpinning and when do I need it?",
            a: "Underpinning is the process of deepening or strengthening your existing foundation, usually by excavating beneath it in sections and pouring new footings lower down. Homeowners most often want it for basement lowering — gaining real ceiling height to turn a low, unusable basement into a legal living space, a suite, or a proper rec room. It's also used to stabilize a foundation that has settled or to support an addition. If you're considering underpinning a basement in **Brampton** or basement lowering across the GTA, it's a significant structural project, so it should always start with an engineer's design and a contractor who has done it before.",
        },
        {
            q: "Do I need a permit for underpinning or foundation work in Ontario?",
            a: "Yes — in Ontario, underpinning and basement lowering are structural work and require a building permit, along with stamped drawings from a licensed structural engineer. Many foundation repairs that alter the structure do too, while minor cosmetic brick or parging work generally does not. Permit requirements and inspections are handled at the municipal level, so the exact process differs slightly between **Oakville**, **Brampton**, and **Toronto**. We help coordinate the engineering and permit paperwork so your project is inspected and signed off correctly — protecting both your home's value and your future resale.",
        },
    ],
};
